import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { InventoryTransaction, InventoryTransactionType } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/currency';
import { formatDateTime } from '../../utils/date';

const transactionNoteTranslations: Record<string, string> = {
  'inventory.transactionNotes.batchToSimple': 'inventory.transactionNotes.batchToSimple',
  'inventory.transactionNotes.stockConsolidated': 'inventory.transactionNotes.stockConsolidated',
  'inventory.transactionNotes.legacyRowConsolidated': 'inventory.transactionNotes.legacyRowConsolidated',
  'inventory.transactionNotes.legacySimpleToBatch': 'inventory.transactionNotes.legacySimpleToBatch',
  'inventory.transactionNotes.simpleToBatch': 'inventory.transactionNotes.simpleToBatch',
  'Batch tracking disabled; stock consolidated into simple inventory': 'inventory.transactionNotes.batchToSimple',
  'Stock consolidated into simple inventory': 'inventory.transactionNotes.stockConsolidated',
  'Legacy simple inventory row consolidated during batch transition': 'inventory.transactionNotes.legacyRowConsolidated',
  'Legacy simple inventory converted to batch tracking': 'inventory.transactionNotes.legacySimpleToBatch',
  'Simple inventory converted to batch tracking': 'inventory.transactionNotes.simpleToBatch',
};

interface TransactionHistoryViewProps {
  transactions: InventoryTransaction[];
  loading: boolean;
  onRefresh: () => void;
  onBatchClick?: (batchId: string) => void;
}

const TransactionHistoryView: React.FC<TransactionHistoryViewProps> = ({
  transactions,
  loading,
  onRefresh,
  onBatchClick,
}) => {
  const { t, language } = useLanguage();
  const localizeTransactionNote = (note: string) => {
    const translationKey = transactionNoteTranslations[note];
    return translationKey ? t(translationKey) : note;
  };
  const typeStyles = {
    [InventoryTransactionType.ADD]: { label: t('inventory.transactionType.add'), color: 'success' as const },
    [InventoryTransactionType.REMOVE]: { label: t('inventory.transactionType.remove'), color: 'warning' as const },
    [InventoryTransactionType.ADJUST]: { label: t('inventory.transactionType.adjust'), color: 'info' as const },
    [InventoryTransactionType.SALE]: { label: t('inventory.transactionType.sale'), color: 'default' as const },
    [InventoryTransactionType.MODE_CHANGE]: { label: t('inventory.transactionType.modeChange'), color: 'info' as const },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Alert severity="info">
        {t('inventory.noTransactions')}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('inventory.transactionHistory')} ({transactions.length})
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('inventory.type')}</TableCell>
              <TableCell sx={{ width: '150px', minWidth: '100px', maxWidth: '200px', resize: 'horizontal', overflow: 'auto' }}>
                {t('inventory.batchNumber')}
              </TableCell>
              <TableCell align="right" sx={{ width: '250px' }}>
                {t('inventory.change')}
              </TableCell>
              <TableCell sx={{ width: '120px' }}>
                {t('inventory.user')}
              </TableCell>
              <TableCell sx={{ width: '180px' }}>
                {t('inventory.date')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell><Chip size="small" variant="outlined" label={typeStyles[transaction.transactionType]?.label || transaction.transactionType} color={typeStyles[transaction.transactionType]?.color || 'default'} /></TableCell>
                <TableCell sx={{ width: '150px' }}>
                  {(transaction.batchId || transaction.batch) ? (
                    <Chip
                      label={transaction.batchNumber || transaction.batch?.batchNumber || t('inventory.noBatchNumber')}
                      size="small"
                      variant="outlined"
                      onClick={onBatchClick && (transaction.batchId || transaction.batch?.id)
                        ? () => onBatchClick(transaction.batchId || transaction.batch!.id)
                        : undefined}
                      sx={{ 
                        cursor: onBatchClick && (transaction.batchId || transaction.batch?.id) ? 'pointer' : 'default',
                        '&:hover': onBatchClick && (transaction.batchId || transaction.batch?.id) ? {
                          backgroundColor: 'action.hover'
                        } : {}
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Box>
                    {/* Show quantity change if any */}
                    {transaction.quantityChange !== 0 && (
                      <Typography
                        variant="body2"
                        color={transaction.quantityChange > 0 ? 'success.main' : 'error.main'}
                        fontWeight="medium"
                      >
                        {transaction.quantityChange > 0 ? '+' : ''}{transaction.quantityChange} {t('inventory.units')}
                      </Typography>
                    )}
                    
                    {/* Parse and show all changes from notes */}
                    {transaction.notes && transaction.notes.trim() !== '' && transaction.notes.split('|').map((part, index) => {
                      const trimmedPart = part.trim();
                      
                      if (trimmedPart.startsWith('PRICE_CHANGE:')) {
                        const prices = trimmedPart.substring(13).split('→');
                        const oldPrice = prices[0] ? formatCurrency(parseFloat(prices[0])) : '';
                        const newPrice = prices[1] ? formatCurrency(parseFloat(prices[1])) : '';
                        return (
                          <Typography
                            key={`price-${index}`}
                            variant="body2"
                            color="primary.main"
                            fontWeight="medium"
                          >
                            {t('inventory.price')}: {oldPrice} → {newPrice}
                          </Typography>
                        );
                      } else if (trimmedPart.startsWith('BATCH_CHANGE:')) {
                        const batchChange = trimmedPart.substring(13).replace('→', ' → ');
                        return (
                          <Typography
                            key={`batch-${index}`}
                            variant="body2"
                            color="info.main"
                            fontWeight="medium"
                          >
                            {t('inventory.batchNumber')}: {batchChange}
                          </Typography>
                        );
                      } else if (trimmedPart !== '') {
                        // User notes (not auto-generated)
                        return (
                          <Typography
                            key={`note-${index}`}
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {localizeTransactionNote(trimmedPart)}
                          </Typography>
                        );
                      }
                      return null;
                    })}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {transaction.createdByUsername
                      ? `@${transaction.createdByUsername}`
                      : transaction.createdBy
                        ? `@${transaction.createdBy.username}`
                        : t('common.system')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                  {formatDateTime(transaction.createdAt, language)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('inventory.transactionSummary')}
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {t('inventory.totalTransactions')}: {transactions.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inventory.additions')}: {transactions.filter(t => t.transactionType === InventoryTransactionType.ADD).length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inventory.removals')}: {transactions.filter(t => t.transactionType === InventoryTransactionType.REMOVE).length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inventory.adjustments')}: {transactions.filter(t => t.transactionType === InventoryTransactionType.ADJUST).length}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TransactionHistoryView;
